import React, { FC } from 'react';
import classNames from 'classnames';
import DataTable from '../dataTable/DataTable';
import {
  TABLE_HEADING_ANALYSIS,
  TABLE_HEADING_ANALYSIS_LIST
} from '../../utils/constants';
import { standardDateFormat } from '../../utils/dateFormatter';
import {
  formatPercentageChange,
  wholeNumber
} from '../../utils/numberFormatter';
import PastPerformanceStatement from '../../components/styled/PastPerformanceStatement';
import TotalReturnStatement from '../styled/TotalReturnStatement';

import styles from './AnalysisTable.module.scss';
import NumericChange from '../shared/NumericChange';

type Props = {
  selectedAccount: AssetAccount;
};

const AnalysisTable: FC<Props> = ({ selectedAccount }) => {
  if (selectedAccount === undefined) {
    return null;
  }

  const { analysis } = selectedAccount.monthlyReport;

  if (analysis === undefined) {
    return null;
  }

  const formatPercOrDefault = (num: number | undefined) => {
    return num === undefined ? '-' : formatPercentageChange(num, 1);
  };
  const monthlyReportAnalysis = selectedAccount.monthlyReport.analysis;
  let accountAnalysis = monthlyReportAnalysis;

  // this is needed because of the data shape, it includes inception data with each underlying object instead of a its own object
  const shapeInceptionRow = (rowCopy) => {
    return {
      ...rowCopy,
      dateRange: 'Inception - ' + standardDateFormat(rowCopy.inceptionDate),
      capitalChange: 'N/A',
      totalInvestment: rowCopy.inceptionCapitalInvestment,
      marketValue: rowCopy.inceptionMVEndofPeriod,
      earnedIncome: 'N/A',
      reinvestedIncome: 'N/A',
      yearToDate: 'N/A',
      sinceInception: 'N/A'
    };
  };

  const inceptionRow =
    monthlyReportAnalysis && monthlyReportAnalysis.length
      ? shapeInceptionRow(monthlyReportAnalysis[0])
      : null;

  if (inceptionRow) {
    accountAnalysis = [inceptionRow, ...accountAnalysis];
  }

  const analysisData = accountAnalysis.map((data, ind) => ({
    [TABLE_HEADING_ANALYSIS.PAST_PERIODS]: data.dateRange,
    [TABLE_HEADING_ANALYSIS.CONTRIBUTIONS_WITHDRAWALS]: (
      <NumericChange
        formatFunction={ind === 0 ? (a) => a.toString() : wholeNumber}
        value={data.contributions}
      />
    ),
    [TABLE_HEADING_ANALYSIS.CAPITAL_CHANGE]: (
      <NumericChange
        formatFunction={ind === 0 ? (a) => a.toString() : wholeNumber}
        value={data.capitalChange}
      />
    ),
    [TABLE_HEADING_ANALYSIS.CAPITAL_INVESTMENT]: wholeNumber(
      data.totalInvestment
    ),
    [TABLE_HEADING_ANALYSIS.MARKET_VALUE_EOP]: wholeNumber(data.marketValue),
    [TABLE_HEADING_ANALYSIS.EARNED_INCOME]:
      ind === 0 ? data.earnedIncome : wholeNumber(data.earnedIncome),
    [TABLE_HEADING_ANALYSIS.REINVESTED_INCOME]:
      ind === 0 ? data.reinvestedIncome : wholeNumber(data.reinvestedIncome),
    [TABLE_HEADING_ANALYSIS.YTD_RETURN_PERCENT]: (
      <NumericChange
        formatFunction={ind === 0 ? (a) => a.toString() : formatPercOrDefault}
        value={data.yearToDate}
      />
    ),
    [TABLE_HEADING_ANALYSIS.INCEPTION_RETURN_PERCENT]: (
      <NumericChange
        formatFunction={ind === 0 ? (a) => a.toString() : formatPercOrDefault}
        value={data.sinceInception}
      />
    ),
    rowClass: classNames(styles['analysis-row'], {
      [styles['top-row']]: ind === 0
    })
  }));

  const columnClasses = {
    [TABLE_HEADING_ANALYSIS.PAST_PERIODS]: styles['period-text']
  };

  const { asOfDate } = selectedAccount.monthlyReport;

  return (
    <div className={styles['table-container']}>
      <div className={styles['title-section']}>
        <span className={styles['table-title']}>Portfolio Analysis</span>
        <span className={styles['table-date']}>
          As of {standardDateFormat(asOfDate)}
        </span>
      </div>
      <DataTable
        headers={TABLE_HEADING_ANALYSIS_LIST}
        rowData={analysisData}
        columnClasses={columnClasses}
        tableFormatClass={styles['analysis-table-layout']}
        labelHeader={TABLE_HEADING_ANALYSIS.PAST_PERIODS}
        valueHeader={TABLE_HEADING_ANALYSIS.MARKET_VALUE_EOP}
      />
      <div className={styles['disclaimer']}>
        <PastPerformanceStatement>
          <div className={styles['legalDisclaimer']}>
            Performance figures shown are cumulative for all time periods.
          </div>
          <TotalReturnStatement />
        </PastPerformanceStatement>
      </div>
    </div>
  );
};

export default AnalysisTable;
